import { Form as FormikForm, Formik } from 'formik';
import PropTypes from 'prop-types';
import { useCallback, useRef, useState } from 'react';

import { Avatar } from '@hivebrite/design-system/Avatar';
import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { toast } from '@hivebrite/design-system/Toast';
import { useIsMobileDevice } from '@hivebrite/react-hooks';

import { NotifyPeopleTriggerButton } from 'hb-react/frontoffice/components/NotifyPeopleModal/NotifyPeopleTriggerButton';
import UserTargeting from 'hb-react/frontoffice/components/user_targeting';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import mentionsOptions from 'hb-react/shared/components/DesignSystem/QuillEditor/utils/mentionsOptions';
import {
  Select,
  Hidden,
} from 'hb-react/shared/components/formik/components/inputs';
import QuillEditorWrappedV3 from 'hb-react/shared/components/QuillEditorWrappedV3';
import { axiosWithSyntaxHandlers as axios } from 'hb-react/shared/utils/axios';
import { isFileExternalSource } from 'hb-react/shared/utils/files/fileContentTypes';

import { ContentThumbnail } from './components/ContentThumbnail';
import { localizedTypeName } from './utils';

const ShareContentModal = ({
  onClose,
  content,
  shareToLiveFeedUrl,
  sharingOptions,
  sharedFrom,
  sharedUrl,
  userTargetingNetwork,
  userTargetingTopics,
}) => {
  const { currentUser } = useDefaultAppContexts();
  const isMobileDevice = useIsMobileDevice();

  const formikSetFieldValueRef = useRef();

  const [notifyPeoples, setNotifyPeoples] = useState([]);

  const typeName = localizedTypeName(content.file_type || content.type);
  const userIsOwner = currentUser.id === content.owner?.id;

  const onSubmit = async (values) => {
    try {
      await axios.post(shareToLiveFeedUrl, {
        ...values,
        topic_id: /topic_(\d+)/.exec(values.shared_in)?.[1],
      });

      onClose();
      toast.success(
        s__('Portfolio|%{typeName} successfully shared!').i__({
          typeName,
        }),
      );
    } catch (error) {
      toast.error(
        s__(`Portfolio|An error occurred: %{error}`).i__({
          error: error.response.data?.errors_full_messages?.[0],
        }),
      );
    }
  };

  const handleValidateNotifySelection = useCallback(
    (selectedUsers, selectedUsersIds) => {
      if (!isMobileDevice) {
        setNotifyPeoples(selectedUsers);
      }
      if (formikSetFieldValueRef.current) {
        formikSetFieldValueRef.current('targeted_user_ids', selectedUsersIds);
      }
    },
    [isMobileDevice],
  );

  return (
    <ModalWithTemplate
      header={
        userIsOwner
          ? s__('SharingModal|Share your %{typeName}').i__({
              typeName,
            })
          : s__('SharingModal|Share this %{typeName}').i__({
              typeName,
            })
      }
      onCloseModal={onClose}
    >
      <Box flexDirection="column" width="100%">
        <Formik
          initialValues={{
            share_message: '',
            shared_in: sharingOptions[0].value,
            shared_from: sharedFrom,
            shared_url: isFileExternalSource(content.file_type)
              ? content.url
              : sharedUrl,
          }}
          onSubmit={onSubmit}
        >
          {({ isSubmitting, values, setFieldValue }) => {
            formikSetFieldValueRef.current = setFieldValue;

            const topicId = /topic_(\d+)/.exec(values.shared_in)?.[1];
            const showNotifyUsersButton = topicId
              ? userTargetingTopics?.authorized
              : userTargetingNetwork?.authorized;
            const maxTargetedUsers = topicId
              ? userTargetingTopics?.maxTargetedUsers
              : userTargetingNetwork?.maxTargetedUsers;
            const topicIds = topicId ? [topicId] : null;
            // Based off post_type in 'app/operations/livefeed/share_content.rb'
            const targetingType = topicId
              ? 'Posts::TopicPost'
              : 'Posts::FeedPost';

            return (
              <FormikForm>
                <Box justifyContent="stretch">
                  <Avatar
                    src={currentUser.photo_thumb_url}
                    size={60}
                    alt={__('%{userName} profil picture').i__({
                      userName: currentUser.name,
                    })}
                  />
                  <Box ml={3} mb={6} flex="1" flexDirection="column">
                    <QuillEditorWrappedV3
                      id="share_message"
                      name="share_message"
                      isDisabled={isSubmitting}
                      isRequired
                      maxLength={1024}
                      minHeight={50}
                      options={{
                        embedLy: false,
                        toolbar: {
                          emojiPicker: true,
                          mentions: mentionsOptions,
                        },
                      }}
                      placeholder={s__(
                        'SharingModal|Share your thoughts and plans with your community...',
                      )}
                    />
                  </Box>
                </Box>

                {sharingOptions?.length === 1 ? (
                  <Hidden name="shared_in" />
                ) : (
                  <Select
                    name="shared_in"
                    label={s__('SharingModal|In Livefeed')}
                    options={sharingOptions}
                    wrapperProps={{ mb: 4 }}
                    placeholder={s__(
                      'SharingModal|Choose in which Livefeed you want to share...',
                    )}
                  />
                )}

                <Hidden name="shared_from" />
                <Hidden name="shared_url" />

                <ContentThumbnail content={content} sharedFrom={sharedFrom} />

                <Box justifyContent="flex-end" mt={4}>
                  {showNotifyUsersButton && (
                    <UserTargeting
                      defaultSelectedUsers={
                        !isMobileDevice ? notifyPeoples : []
                      }
                      renderTriggerElement={(onClick) => (
                        <NotifyPeopleTriggerButton
                          values={values}
                          isDisabled={isSubmitting}
                          mr={2}
                          onClick={onClick}
                        />
                      )}
                      baseQuery={{
                        targeting_type: targetingType,
                        topic_ids: topicIds,
                      }}
                      maxTargetedUsers={maxTargetedUsers}
                      onValidateSelection={handleValidateNotifySelection}
                    />
                  )}
                  <Button type="submit" isLoading={isSubmitting}>
                    {__('Publish')}
                  </Button>
                </Box>
              </FormikForm>
            );
          }}
        </Formik>
      </Box>
    </ModalWithTemplate>
  );
};

export default ShareContentModal;

ShareContentModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  content: PropTypes.object.isRequired,
  shareToLiveFeedUrl: PropTypes.string.isRequired,
  sharingOptions: PropTypes.array.isRequired,
  sharedFrom: PropTypes.string.isRequired,
  sharedUrl: PropTypes.string.isRequired,
  userTargetingNetwork: PropTypes.shape({
    authorized: PropTypes.bool.isRequired,
    maxTargetedUsers: PropTypes.number.isRequired,
  }),
  userTargetingTopics: PropTypes.shape({
    authorized: PropTypes.bool.isRequired,
    maxTargetedUsers: PropTypes.number.isRequired,
  }),
};
